/**
 * Every sign-up attributed to where the visitor came from (ATTR-1, Anand,
 * 26 Oct 26). Mounted once inside the Router, beside SiteTrafficBeacon.
 *
 * On every page: the page's touch is kept (src/lib/attribution/keeper.ts
 * noteTouch). The touches an in-app browser hands over (?fly_src=) are taken
 * in at boot, before React renders (src/entry-client.tsx takeHandoffArrival).
 *
 * Whenever a session appears (Google's round trip, an email link, a password
 * sign-in, a tab that was already signed in) for an account made in the last
 * 72 hours that this browser has not reported yet, the touches go to
 * gbp-agent-portal op signup_attribution, which writes the account's row once
 * and tells every configured ad platform in the background
 * (CompleteRegistration). Its answer carries the event id: the page's own
 * tags (the Meta pixel, GTM) fire the same conversion under it, so each
 * platform counts one, and never on a private page, which loads no tag.
 * Every sign-up path ends on some Fly page signed in, so this one place
 * covers them all: the start page, the landing's Sign in, the Fly app's
 * login, a confirmation email, the connect link.
 *
 * Never in the way: no await on a render, an 8 second budget on the call,
 * failures logged and tried again on a later page (three tries a tab), and
 * nothing at all on an agency's white-label host, an embed or inside a frame.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { attributionSkipped, noteTouch } from '@/lib/attribution/keeper';
import { isFreshAccount, recordSignup, signupReported } from '@/lib/attribution/signupRecord';

export function SignupAttribution() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (attributionSkipped(pathname)) return;
    noteTouch();
  }, [pathname]);

  useEffect(() => {
    if (attributionSkipped(window.location.pathname)) return;
    let busy = false;
    const check = (session: Session | null) => {
      const user = session?.user;
      if (!user || busy || !isFreshAccount(user.created_at) || signupReported(user.id)) return;
      busy = true;
      void recordSignup(user.id).finally(() => {
        busy = false;
      });
    };
    void supabase.auth
      .getSession()
      .then(({ data }) => check(data.session))
      .catch(() => undefined);
    // Supabase calls this inside its auth lock, and the call above reads the
    // session again, so it waits for the next tick.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') window.setTimeout(() => check(session), 0);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  return null;
}
